import { Injectable, signal, computed } from '@angular/core';

export type FeedbackModalType = 'success' | 'error' | 'warning' | 'confirm' | 'prompt';

export interface FeedbackModalState {
  visible: boolean;
  type: FeedbackModalType;
  title: string;
  text: string;
  buttonLabel: string;
  cancelLabel: string;
  onConfirm: (() => void) | null;
  // Configuración del prompt (textarea opcional)
  promptLabel: string;
  promptPlaceholder: string;
  promptMaxLength: number | null;
  promptValue: string;
  onPromptConfirm: ((value: string) => void) | null;
}

const DEFAULT_STATE: FeedbackModalState = {
  visible: false,
  type: 'success',
  title: '',
  text: '',
  buttonLabel: 'Aceptar',
  cancelLabel: '',
  onConfirm: null,
  promptLabel: '',
  promptPlaceholder: '',
  promptMaxLength: null,
  promptValue: '',
  onPromptConfirm: null,
};

@Injectable({ providedIn: 'root' })
export class FeedbackModalService {
  private readonly state = signal<FeedbackModalState>(DEFAULT_STATE);

  readonly visible = computed(() => this.state().visible);
  readonly type = computed(() => this.state().type);
  readonly title = computed(() => this.state().title);
  readonly text = computed(() => this.state().text);
  readonly buttonLabel = computed(() => this.state().buttonLabel);
  readonly cancelLabel = computed(() => this.state().cancelLabel);
  readonly isConfirm = computed(() => this.state().type === 'confirm');
  readonly isPrompt = computed(() => this.state().type === 'prompt');
  readonly promptLabel = computed(() => this.state().promptLabel);
  readonly promptPlaceholder = computed(() => this.state().promptPlaceholder);
  readonly promptMaxLength = computed(() => this.state().promptMaxLength);
  readonly promptValue = computed(() => this.state().promptValue);

  showSuccess(config: { title?: string; text: string; buttonLabel?: string }): void {
    this.state.set({ ...DEFAULT_STATE, visible: true, type: 'success', title: config.title ?? '', text: config.text, buttonLabel: config.buttonLabel ?? 'Aceptar' });
  }

  showError(config: { title?: string; text: string; buttonLabel?: string }): void {
    this.state.set({ ...DEFAULT_STATE, visible: true, type: 'error', title: config.title ?? '', text: config.text, buttonLabel: config.buttonLabel ?? 'Aceptar' });
  }

  showWarning(config: { title?: string; text: string; buttonLabel?: string }): void {
    this.state.set({ ...DEFAULT_STATE, visible: true, type: 'warning', title: config.title ?? '', text: config.text, buttonLabel: config.buttonLabel ?? 'Aceptar' });
  }

  /** Modal de confirmacion con 2 botones */
  showConfirm(config: { title?: string; text: string; confirmLabel?: string; cancelLabel?: string; onConfirm: () => void }): void {
    this.state.set({
      ...DEFAULT_STATE,
      visible: true,
      type: 'confirm',
      title: config.title ?? '',
      text: config.text,
      buttonLabel: config.confirmLabel ?? 'Confirmar',
      cancelLabel: config.cancelLabel ?? 'Cancelar',
      onConfirm: config.onConfirm,
    });
  }

  /**
   * Modal con textarea para capturar texto libre antes de confirmar.
   * `onConfirm` recibe el valor del textarea (string, posiblemente vacío).
   */
  showPrompt(config: {
    title?: string;
    text: string;
    promptLabel?: string;
    promptPlaceholder?: string;
    promptMaxLength?: number;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: (value: string) => void;
  }): void {
    this.state.set({
      ...DEFAULT_STATE,
      visible: true,
      type: 'prompt',
      title: config.title ?? '',
      text: config.text,
      buttonLabel: config.confirmLabel ?? 'Confirmar',
      cancelLabel: config.cancelLabel ?? 'Cancelar',
      promptLabel: config.promptLabel ?? '',
      promptPlaceholder: config.promptPlaceholder ?? '',
      promptMaxLength: config.promptMaxLength ?? null,
      promptValue: '',
      onPromptConfirm: config.onConfirm,
    });
  }

  setPromptValue(value: string): void {
    const max = this.state().promptMaxLength;
    const next = max != null && value.length > max ? value.slice(0, max) : value;
    this.state.update((s) => ({ ...s, promptValue: next }));
  }

  confirm(): void {
    const s = this.state();
    if (s.type === 'prompt') {
      const fn = s.onPromptConfirm;
      const value = s.promptValue;
      this.hide();
      if (fn) fn(value);
      return;
    }
    const fn = s.onConfirm;
    this.hide();
    if (fn) fn();
  }

  hide(): void {
    this.state.set(DEFAULT_STATE);
  }
}
